import ExamFlowPhases from 'constants/exam-flow-phases';
import cloneDeep from 'lodash/cloneDeep';
import forIn from 'lodash/forIn';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import {v4 as uuidv4} from 'uuid';

import type {ExamFlowPhase} from 'constants/exam-flow-phases';
import type {Map} from 'immutable';
import type {ExamPhaseType, FlowPlanPhase, FlowPlanTimer} from 'types/flow-plan-types';
import type {ImmutableMap} from 'types/immutable-types';

//#region utility functions

export const getBlockHasEvaluation = (
    block,
    viewName = undefined,
    examPhase: ExamFlowPhase = ExamFlowPhases.POST_ENCOUNTER,
) => {
    let hasEvaluation = false;
    const phase = block.find((p) => p.type === examPhase);

    if (!isNil(phase)) {
        hasEvaluation = get(phase, ['views', viewName, 'hasEval']) === true;
    }

    return hasEvaluation;
};

/**
 * Determine whether a block has door notes enabled.
 */
export const getBlockHasDoorNote = (block: Array<Immutable.FromJS<FlowPlanPhase>>) =>
    block.some((phase) => phase.get('type') === ExamFlowPhases.DOOR_NOTES);

/**
 * Determine whether a block has in-room patient evaluations enabled.
 */
export const getBlockHasInRoomPatientEvaluation = (block: unknown[]) =>
    getBlockHasEvaluation(block, 'patient', ExamFlowPhases.ENCOUNTER);

export const getImmutableBlockHasInRoomPatientEvaluation = (block: ImmutableMap<unknown>) => {
    return getBlockHasEvaluation(block.toJS(), 'patient', ExamFlowPhases.ENCOUNTER);
};

/**
 * Determine whether a block has learner evaluations enabled.
 */
export const getBlockHasLearnerEvaluation = (block: unknown[]) => getBlockHasEvaluation(block, 'learner');

/**
 * Determine whether a block has patient evaluations enabled.
 */
export const getBlockHasPatientEvaluation = (block: unknown[]) => getBlockHasEvaluation(block, 'patient');

/**
 * Determine whether a block has EMRs.
 */
// @ts-expect-error TS(2339) FIXME: Property 'type' does not exist on type 'unknown'.
export const getBlockHasEmr = (block: unknown[]) => block.some((p) => p.type === ExamFlowPhases.EMR);

/**
 * Determine whether a block has at least one phase enabled (excluding waiting room and start recording phases).
 */
export const getBlockHasAtLeastOnePhase = (block: Array<FlowPlanPhase | Immutable.FromJS<FlowPlanPhase>>) => {
    const relevantPhases: ExamFlowPhase[] = [
        ExamFlowPhases.DOOR_NOTES,
        ExamFlowPhases.EMR,
        ExamFlowPhases.ENCOUNTER,
        ExamFlowPhases.POST_ENCOUNTER,
    ];

    return block.some((phase) => {
        // @ts-expect-error TS(2339) FIXME: Property 'type' does not exist on type 'FlowPlanPhase | FromJS<FlowPlanPhase>'.
        const phaseType = typeof phase.get === 'function' ? phase.get('type') : phase.type;
        return relevantPhases.includes(phaseType as ExamFlowPhase);
    });
};

/**
 * Takes a flow plan, which is a flat list of phases, and returns a list of
 * blocks. Each block is a list of phases belonging to a block. The reverse
 * function is getFlattenedFlowPlan()
 *
 * @param flowPlan
 */
export const getFlowPlanBlocks = (flowPlan): Array<Array<FlowPlanPhase>> => {
    return Object.values(groupBy(flowPlan, (phase) => phase.blockId));
};

/**
 * SCLD-19278 Default templates are missing the configuration values for hiding timers, and the new
 * isRecordingEnabled property, so until we backfill those, we have to normalize them in the code.
 */
export const backfillFlowPlanChanges = (flowPlanBlocks: Array<Array<FlowPlanPhase>>): Array<Array<FlowPlanPhase>> => {
    return flowPlanBlocks.map((block) => {
        const phases = enableBlockEncounterRecording(block);
        return phases.map((phase) => {
            const timers = phase.timers?.map((timer) => {
                return isNil(timer.shouldHideTimer)
                    ? {
                          ...timer,
                          shouldHideTimer: {
                              learner: false,
                              sp: false,
                          },
                      }
                    : timer;
            });

            return {
                ...phase,
                timers,
            };
        });
    });
};

/**
 * For the default examFlowTemplate, generates a JS copy of the flowplan with new
 * timer uuids, fixes the inputs to have this new timer id, and hydrates the
 * flowPlan into blocks for use as a follow-up block
 */
export const generateFollowUpFlowPlan = (
    defaultExamFlowTemplate: Map<string, unknown>,
): Array<Array<FlowPlanPhase>> => {
    const defaultFlowPlan = getFlowPlanBlocks(
        defaultExamFlowTemplate
            .get('flowPlan')
            // @ts-expect-error TS2339: Property toJS does not exist on type unknown
            .toJS(),
    );
    const defaultFlowPlanWithNewTimerUuids = defaultFlowPlan.map((block) => {
        return block.map((phase) => {
            const oldAndNewTimerIds = {};
            // duplicate timer ids within a flowPlan can crash the exam
            phase.timers = phase.timers.map((timer) => {
                const oldInputId = timer.inputId;
                timer.inputId = uuidv4();
                oldAndNewTimerIds[oldInputId] = timer.inputId;
                return timer;
            });
            // replace references to old timer id with new id
            phase.inputs = phase.inputs.map((inputArray) => {
                return inputArray.map((inputId) => {
                    return !isNil(oldAndNewTimerIds[inputId]) ? oldAndNewTimerIds[inputId] : inputId;
                });
            });
            return phase;
        });
    });

    return backfillFlowPlanChanges(defaultFlowPlanWithNewTimerUuids);
};

/**
 * For the default examFlowTemplate, generates a JS copy of the flowplan with new
 * timer uuids, fixes the inputs to have this new timer id, and hydrates the
 * flowPlan into system automated blocks for use as a follow-up block.
 *
 * @param {Immutable.Map} defaultExamFlowTemplate
 */
export const generateSystemAutomatedFollowUpFlowPlan = (defaultExamFlowTemplate): Array<Array<FlowPlanPhase>> => {
    const defaultFlowPlan = getFlowPlanBlocks(defaultExamFlowTemplate.get('flowPlan').toJS());
    const defaultSystemAutomatedFlowPlan = defaultFlowPlan.map((block) => {
        block = disableStartEncounter(cloneDeep(block));
        block.forEach((phase) => {
            if (phase.type === ExamFlowPhases.POST_ENCOUNTER) {
                block = switchToSystemAutomatedPostEncounterPhase(cloneDeep(block));
            } else if (phase.type === ExamFlowPhases.ENCOUNTER) {
                block = enableSystemAutomatedEncounter(cloneDeep(block));
            } else if (phase.type === ExamFlowPhases.EMR) {
                block = disableEmr(cloneDeep(block));
                block = enableSystemAutomatedEmr(cloneDeep(block));
            } else if (phase.type === ExamFlowPhases.DOOR_NOTES) {
                block = disableDoorNotes(cloneDeep(block));
                block = enableSystemAutomatedDoorNotes(cloneDeep(block));
            }
        });
        return block;
    });
    const defaultFlowPlanWithNewTimerUuids = defaultSystemAutomatedFlowPlan.map((block) => {
        return block.map((phase) => {
            const oldAndNewTimerIds = {};
            // duplicate timer ids within a flowPlan can crash the exam
            phase.timers = phase.timers.map((timer) => {
                const oldInputId = timer.inputId;
                timer.inputId = uuidv4();
                oldAndNewTimerIds[oldInputId] = timer.inputId;
                timer.isHardTimer = true;
                return timer;
            });
            // replace references to old timer id with new id
            phase.inputs = phase.inputs.map((inputArray) => {
                return inputArray.map((inputId) => {
                    return !isNil(oldAndNewTimerIds[inputId]) ? oldAndNewTimerIds[inputId] : inputId;
                });
            });
            return phase;
        });
    });
    return backfillFlowPlanChanges(defaultFlowPlanWithNewTimerUuids);
};

/**
 * Takes list of blocks used in flow plan forms and converts it into a flat list
 * of phases, which is the the expected structure of flow plans. The reverse
 * function is getFlowPlanBlocks()
 *
 * @param {Array} flowPlanBlocks
 * @returns {Array} flowPlan
 */
export const getFlattenedFlowPlan = (flowPlanBlocks) => {
    let phaseId = -1;

    return flowPlanBlocks
        .map((block, blockId) => {
            return block.map((phase) => {
                phaseId++;

                return {
                    ...phase,
                    blockId,
                    phaseId,
                };
            });
        })
        .flat();
};

/**
 * Takes a block of phases and returns an object where each key is the phase
 * name and its value is the corresponding phase. This function is useful to
 * find a particular phase of a block, or to determine whether some phase exists
 * within a block.
 *
 * @param {Array} block
 * @returns {object}
 */
export const getBlockPhasesByType = (block) => {
    const blockPhaseByType = {};

    forIn(ExamFlowPhases, (type) => {
        blockPhaseByType[type] = block.find((p) => p.type === type);
    });

    return blockPhaseByType;
};

/**
 * Creates an exam flow description based on phases in a block.
 *
 * TODO: SCLD-8053 this function preserves existing functionality from
 * shared-exam-flow-templates.js. Rethink utility of exam flow descriptions.
 *
 * @param {object} block
 * @param {boolean} showHonorCode
 * @returns {string}
 */
export const getExamFlowDescription = (block, showHonorCode = false) => {
    const phasesByType = getBlockPhasesByType(block);
    const descriptionList = [];

    if (showHonorCode) {
        descriptionList.push('Honor Code');
    }
    if (!isNil(phasesByType[ExamFlowPhases.WAITING_ROOM])) {
        descriptionList.push('Waiting Room');
    }
    if (!isNil(phasesByType[ExamFlowPhases.DOOR_NOTES])) {
        descriptionList.push('Door Note');
    }
    if (!isNil(phasesByType[ExamFlowPhases.EMR])) {
        descriptionList.push('Exam EMR');
    }
    if (!isNil(phasesByType[ExamFlowPhases.ENCOUNTER])) {
        descriptionList.push('Encounter Recording');
    }
    if (!isNil(phasesByType[ExamFlowPhases.POST_ENCOUNTER])) {
        if (getBlockHasPatientEvaluation(block)) {
            descriptionList.push('SP Evaluation');
        }
        if (getBlockHasLearnerEvaluation(block)) {
            descriptionList.push('Learner Evaluation');
        }
    }

    // If no phases are present, return a default message
    if (descriptionList.length === 0) {
        return 'No phases configured';
    }

    let description = descriptionList.join(' + ');
    if (!isNil(phasesByType[ExamFlowPhases.STUDENT_START_RECORDING])) {
        description = `Learner Driven: ${description}`;
    }
    return description;
};

/**
 * Creates an exam flow title based on phases in a block.
 *
 * TODO: SCLD-8053 this function preserves existing functionality from
 * shared-exam-flow-templates.js. Rethink utility of exam flow titles.
 *
 * @param {object} block
 * @param {boolean} showHonorCode
 * @returns {string}
 */
export const getExamFlowTitle = (block, showHonorCode = false) => {
    const phasesByType = getBlockPhasesByType(block);
    const titleList = [];

    if (showHonorCode) {
        titleList.push('HC');
    }
    if (!isNil(phasesByType[ExamFlowPhases.WAITING_ROOM])) {
        titleList.push('WR');
    }
    if (!isNil(phasesByType[ExamFlowPhases.DOOR_NOTES])) {
        titleList.push('DN');
    }
    if (!isNil(phasesByType[ExamFlowPhases.EMR])) {
        titleList.push('EMR');
    }
    if (!isNil(phasesByType[ExamFlowPhases.ENCOUNTER])) {
        titleList.push('Enc');
    }
    if (!isNil(phasesByType[ExamFlowPhases.POST_ENCOUNTER])) {
        if (getBlockHasPatientEvaluation(block)) {
            titleList.push('SP Eval');
        }
        if (getBlockHasLearnerEvaluation(block)) {
            titleList.push('Lrn Eval');
        }
    }

    // If no phases are present, return a default message
    if (titleList.length === 0) {
        return 'No phases';
    }

    let title = titleList.join(', ');
    if (!isNil(phasesByType[ExamFlowPhases.STUDENT_START_RECORDING])) {
        title = `LD: ${title}`;
    }
    return title;
};

//#endregion utility functions

/**
 * Idempotently enables waiting room on a block.
 *
 * @param {Array} block
 * @returns {Array}
 */
export const enableStartEncounter = (block) => {
    const encounterPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.ENCOUNTER);
    const startEncounter = block.find((p) => p.type === ExamFlowPhases.START_RECORDING);

    if (encounterPhaseIndex !== -1 && isNil(startEncounter)) {
        block.splice(encounterPhaseIndex, 0, buildStartRecordingPhase());
    }

    return block;
};

/**
 * Idempotently disables start recording on a block.
 *
 * @param {Array} block
 * @returns {Array}
 */
export const disableStartEncounter = (block) => {
    // if learner exists, then make block proctor driven.
    let learnerStartRecordingPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.STUDENT_START_RECORDING);
    if (learnerStartRecordingPhaseIndex !== -1) {
        block = makeBlockProctorDriven(cloneDeep(block));
    }

    const startRecordingPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.START_RECORDING);
    learnerStartRecordingPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.STUDENT_START_RECORDING);

    if (startRecordingPhaseIndex === -1 && learnerStartRecordingPhaseIndex === -1) {
        return block;
    }

    block.splice(startRecordingPhaseIndex, 1);

    return block;
};

/**
 * Idempotently enables waiting room on a block.
 *
 * @param {Array} block
 * @returns {Array}
 */
export const enableWaitingRoom = (block) => {
    const waitingRoomPhase = block.find((p) => p.type === ExamFlowPhases.WAITING_ROOM);
    return isNil(waitingRoomPhase) ? [buildWaitingRoomPhase()].concat(block) : block;
};

/**
 * Idempotently disables waiting room on a block.
 *
 * @param {Array} block
 * @returns {Array}
 */
export const disableWaitingRoom = (block) => {
    if (block[0].type === ExamFlowPhases.WAITING_ROOM) {
        block.splice(0, 1);
    }

    return block;
};

/**
 * Idempotently adds system automated encounter phase on a block.
 *
 * @param {Array} block
 * @returns {Array}
 */
export const enableSystemAutomatedEncounter = (block) => {
    const encounterPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.ENCOUNTER);

    if (encounterPhaseIndex === -1) {
        // Determine where to insert based on the standard phase order:
        // Waiting Room -> Door Notes -> EMR -> Encounter -> Post Encounter
        const emrPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.EMR);
        const doorNotesPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.DOOR_NOTES);
        const waitingRoomPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.WAITING_ROOM);

        let insertIndex: number;
        if (emrPhaseIndex !== -1) {
            // Insert after EMR phase if it exists
            insertIndex = emrPhaseIndex + 1;
        } else if (doorNotesPhaseIndex !== -1) {
            // Insert after door notes phase if EMR doesn't exist but door notes does
            insertIndex = doorNotesPhaseIndex + 1;
        } else if (waitingRoomPhaseIndex !== -1) {
            // Insert after waiting room phase if it's the only earlier phase that exists
            insertIndex = waitingRoomPhaseIndex + 1;
        } else {
            // No earlier phases exist, so insert at the beginning of the block
            insertIndex = 0;
        }

        block.splice(insertIndex, 0, buildSystemAutomatedEncounterPhase());
    } else {
        const showEmr = block[encounterPhaseIndex]?.views?.patient?.showEmr ?? false;
        block.splice(encounterPhaseIndex, 1, buildSystemAutomatedEncounterPhase(showEmr));
    }

    return block;
};

/**
 * Idempotently adds encounter phase on a block.
 *
 * @param {Array} block
 * @returns {Array}
 */
export const enableEncounter = (block) => {
    const encounterPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.ENCOUNTER);
    if (encounterPhaseIndex === -1) {
        const startRecordingPhaseIndex = block.findIndex(
            (p) => p.type === ExamFlowPhases.START_RECORDING || p.type === ExamFlowPhases.STUDENT_START_RECORDING,
        );

        let insertIndex: number;
        if (startRecordingPhaseIndex === -1) {
            // Determine where to insert them based on the standard phase order:
            // Waiting Room -> Door Notes -> EMR -> Start Recording -> Encounter -> Post Encounter
            const emrPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.EMR);
            const doorNotesPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.DOOR_NOTES);
            const waitingRoomPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.WAITING_ROOM);

            if (emrPhaseIndex !== -1) {
                // Insert after EMR phase if it exists
                insertIndex = emrPhaseIndex + 1;
            } else if (doorNotesPhaseIndex !== -1) {
                // Insert after door notes phase if EMR doesn't exist but door notes does
                insertIndex = doorNotesPhaseIndex + 1;
            } else if (waitingRoomPhaseIndex !== -1) {
                // Insert after waiting room phase if it's the only earlier phase that exists
                insertIndex = waitingRoomPhaseIndex + 1;
            } else {
                // No earlier phases exist, so insert at the beginning of the block
                insertIndex = 0;
            }

            // Insert start recording phase at the determined position
            block.splice(insertIndex, 0, buildStartRecordingPhase());
            // Increment index to insert encounter phase right after start recording
            insertIndex++;
        } else {
            // Start recording phase already exists, insert encounter phase immediately after it
            insertIndex = startRecordingPhaseIndex + 1;
        }

        block.splice(insertIndex, 0, buildEncounterPhase());
    } else {
        const showEmr = block[encounterPhaseIndex]?.views?.patient?.showEmr ?? false;
        block.splice(encounterPhaseIndex, 1, buildEncounterPhase(showEmr));
    }
    return block;
};

/**
 * Idempotently removes encounter phase from a block.
 *
 * @param {Array} block
 * @returns {Array}
 */
export const disableEncounter = (block) => {
    block = block.filter(
        (phase) =>
            phase.type !== ExamFlowPhases.START_RECORDING && phase.type !== ExamFlowPhases.STUDENT_START_RECORDING,
    );
    block = block.filter((phase) => phase.type !== ExamFlowPhases.ENCOUNTER);
    return block;
};

/**
 * Idempotently enables learner evaluations on a block.
 *
 * @param block
 */
export const enableLearnerEvaluations = (
    block: Array<Partial<FlowPlanPhase>>,
    isSystemAutomated = false,
): Array<Partial<FlowPlanPhase>> => {
    const postEncounterPhase = block.find((p) => p.type === ExamFlowPhases.POST_ENCOUNTER);

    if (isNil(postEncounterPhase)) {
        if (isSystemAutomated) {
            block.push(buildSystemAutomatedPostEncounterPhase({hasLearnerEvaluation: true}));
        } else {
            block.push(buildPostEncounterPhase({hasLearnerEvaluation: true}));
        }
    } else if (!getBlockHasLearnerEvaluation(block)) {
        const emrPhase = block.find((p) => p.type === ExamFlowPhases.EMR);
        if (isSystemAutomated) {
            block.splice(
                -1,
                1,
                buildSystemAutomatedPostEncounterPhase({
                    hasEmr: !isNil(emrPhase),
                    hasLearnerEvaluation: true,
                    hasPatientEvaluation: getBlockHasPatientEvaluation(block),
                    timers: postEncounterPhase.timers,
                }),
            );
        } else {
            block.splice(
                -1,
                1,
                buildPostEncounterPhase({
                    hasEmr: !isNil(emrPhase),
                    hasLearnerEvaluation: true,
                    hasPatientEvaluation: getBlockHasPatientEvaluation(block),
                    timers: postEncounterPhase.timers,
                }),
            );
        }
    }

    return block;
};

/**
 * Idempotently enables patient evaluations
 *
 * @param block
 */
export const enablePatientEvaluations = (
    block: Array<Partial<FlowPlanPhase>>,
    isSystemAutomated = false,
): Array<Partial<FlowPlanPhase>> => {
    const postEncounterPhase = block.find((p) => p.type === ExamFlowPhases.POST_ENCOUNTER);

    if (isNil(postEncounterPhase)) {
        if (isSystemAutomated) {
            block.push(buildSystemAutomatedPostEncounterPhase({hasPatientEvaluation: true}));
        } else {
            block.push(buildPostEncounterPhase({hasPatientEvaluation: true}));
        }
    } else if (!getBlockHasPatientEvaluation(block)) {
        const emrPhase = block.find((p) => p.type === ExamFlowPhases.EMR);
        if (isSystemAutomated) {
            block.splice(
                -1,
                1,
                buildSystemAutomatedPostEncounterPhase({
                    hasEmr: !isNil(emrPhase),
                    hasLearnerEvaluation: getBlockHasLearnerEvaluation(block),
                    hasPatientEvaluation: true,
                    timers: postEncounterPhase.timers,
                }),
            );
        } else {
            block.splice(
                -1,
                1,
                buildPostEncounterPhase({
                    hasEmr: !isNil(emrPhase),
                    hasLearnerEvaluation: getBlockHasLearnerEvaluation(block),
                    hasPatientEvaluation: true,
                    timers: postEncounterPhase.timers,
                }),
            );
        }
    }

    return block;
};

/**
 * Idempotently disables learner evaluations on a block.
 *
 * @param {Array} block
 * @returns {Array}
 */
export const disableLearnerEvaluations = (block) => {
    const postEncounterPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.POST_ENCOUNTER);

    if (postEncounterPhaseIndex < 0) {
        return block;
    }

    const postEncounterPhase = block[postEncounterPhaseIndex];

    if (postEncounterPhase.views.learner.showEmr === true) {
        delete postEncounterPhase.views.learner.hasEval;
        delete postEncounterPhase.views.learner.evaluationTemplateIds;
    } else if (postEncounterPhase.views.patient.hasEval === true) {
        const learnerInputId = postEncounterPhase.views.learner.inputId;
        postEncounterPhase.inputs[0] = postEncounterPhase.inputs[0].filter((i) => i !== learnerInputId);
        postEncounterPhase.views.learner = {
            message: 'Waiting for patient to complete evaluation.',
        };
    } else {
        block.splice(postEncounterPhaseIndex, 1);
    }

    return block;
};

/**
 * Idempotently disables patient evaluations on a block
 *
 * @param {Array} block
 * @returns {Array}
 */
export const disablePatientEvaluations = (block) => {
    const postEncounterPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.POST_ENCOUNTER);

    if (postEncounterPhaseIndex < 0) {
        return block;
    }

    const postEncounterPhase = block[postEncounterPhaseIndex];

    if (postEncounterPhase.views.learner.showEmr || postEncounterPhase.views.learner.hasEval === true) {
        const patientInputId = postEncounterPhase.views.patient.inputId;
        postEncounterPhase.inputs[0] = postEncounterPhase.inputs[0].filter((i) => i !== patientInputId);
        delete postEncounterPhase.views.patient.hasEval;
        postEncounterPhase.views.patient = {
            message: 'Waiting for learner to complete evaluation.',
        };
    } else {
        block.splice(postEncounterPhaseIndex, 1);
    }

    return block;
};

/**
 * Idempotently disables the post encounter phase on a block.
 *
 * @param {Array} block
 * @returns {Array}
 */
export const disablePostEncounter = (block) => {
    return block.filter((phase) => phase.type !== ExamFlowPhases.POST_ENCOUNTER);
};

/**
 * Idempotently disables door notes for a given block.
 *
 * @param {Array} block
 * @returns {Array}
 */
export const disableDoorNotes = (block) => {
    const waitingRoomPhase = block.find((p) => p.type === ExamFlowPhases.WAITING_ROOM);

    if (!isNil(waitingRoomPhase)) {
        block.splice(1, 1);
    } else if (block[0].type === ExamFlowPhases.DOOR_NOTES) {
        block.splice(0, 1);
    }

    return block;
};

/**
 * Idempotently enables door notes for a given block.
 *
 * @param {Array} block
 * @returns {Array}
 */
export const enableDoorNotes = (block) => {
    const doorNotesPhase = block.find((p) => p.type === ExamFlowPhases.DOOR_NOTES);
    return isNil(doorNotesPhase) ? [buildDoorNotesPhase()].concat(block) : block;
};

/**
 * Idempotently enables door notes for a given block.
 *
 * @param {Array} block
 * @returns {Array}
 */
export const enableSystemAutomatedDoorNotes = (block) => {
    const doorNotesPhase = block.find((p) => p.type === ExamFlowPhases.DOOR_NOTES);
    const waitingRoomPhase = block.find((p) => p.type === ExamFlowPhases.WAITING_ROOM);

    if (!isNil(waitingRoomPhase)) {
        block.splice(1, 0, buildSystemAutomatedDoorNotesPhase());
        return block;
    }

    return isNil(doorNotesPhase) ? [buildSystemAutomatedDoorNotesPhase()].concat(block) : block;
};

/*
 * Restores options that should have no change. The use case is when the user
 * has unchecked and then re-checked an option before attempting to save. Any
 * associations should be reset back to their original values.
 *
 * @param (Array) proposedFlowPlan
 * @param (Array) originalFlowPlan
 * @returns (Array) flowPlan
 */
export const restoreOrphanedAssociations = (proposedFlowPlan, originalFlowPlan) => {
    return proposedFlowPlan.map((phase) => {
        const {blockId, type} = phase;
        const originalPhase = originalFlowPlan.find((p) => p.blockId === blockId && p.type === type);

        if (!isNil(originalPhase)) {
            if (type === ExamFlowPhases.DOOR_NOTES && !isNil(originalPhase.doorNoteId)) {
                phase.doorNoteId = originalPhase.doorNoteId;
            } else if (type === ExamFlowPhases.ENCOUNTER) {
                const inRoomEvaluationTemplateIds = get(originalPhase, ['views', 'patient', 'evaluationTemplateIds']);
                const hasInRoomEvaluation = get(phase, ['views', 'patient', 'hasEval']) === true;

                if (hasInRoomEvaluation && !isEmpty(inRoomEvaluationTemplateIds)) {
                    phase.views.patient.evaluationTemplateIds = inRoomEvaluationTemplateIds;
                }
            } else if (type === ExamFlowPhases.POST_ENCOUNTER) {
                const originalLearnerEvaluationTemplateIds = get(originalPhase, [
                    'views',
                    'learner',
                    'evaluationTemplateIds',
                ]);
                const originalPatientEvaluationTemplateIds = get(originalPhase, [
                    'views',
                    'patient',
                    'evaluationTemplateIds',
                ]);

                const hasLearnerEvaluation = get(phase, ['views', 'learner', 'hasEval']) === true;
                const hasPatientEvaluation = get(phase, ['views', 'patient', 'hasEval']) === true;

                if (hasLearnerEvaluation && !isEmpty(originalLearnerEvaluationTemplateIds)) {
                    phase.views.learner.evaluationTemplateIds = originalLearnerEvaluationTemplateIds;
                }
                if (hasPatientEvaluation && !isEmpty(originalPatientEvaluationTemplateIds)) {
                    phase.views.patient.evaluationTemplateIds = originalPatientEvaluationTemplateIds;
                }
            }
        }

        return phase;
    });
};

/**
 * Idempotently disables EMRs for a given block.
 *
 * @param {Array} block
 * @returns {Array}
 */
export const disableEmr = (block) => {
    const emrPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.EMR);

    if (emrPhaseIndex < 0) {
        return block;
    }

    block.splice(emrPhaseIndex, 1);

    const encounterPhase = block.find((p) => p.type === ExamFlowPhases.ENCOUNTER);
    if (!isNil(encounterPhase)) {
        delete encounterPhase.views.patient.showEmr;
    }

    const postEncounterPhase = block.find((p) => p.type === ExamFlowPhases.POST_ENCOUNTER);
    if (!isNil(postEncounterPhase)) {
        delete postEncounterPhase.views.learner.showEmr;

        if (postEncounterPhase.views.patient.hasEval && postEncounterPhase.views.learner.hasEval !== true) {
            const learnerInputId = postEncounterPhase.views.learner.inputId;
            postEncounterPhase.inputs[0] = postEncounterPhase.inputs[0].filter((i) => i !== learnerInputId);
            postEncounterPhase.views.learner.message = 'Waiting for patient to complete evaluation.';
        } else if (
            postEncounterPhase.views.patient.hasEval !== true &&
            postEncounterPhase.views.learner.hasEval !== true
        ) {
            block.pop();
        }
    }

    return block;
};

/**
 * Idempotently enables EMRs for a given block.
 *
 * @param {Array} block
 * @returns {Array}
 */
export const enableEmr = (block) => {
    const emrPhase = block.find((p) => p.type === ExamFlowPhases.EMR);

    if (!isNil(emrPhase)) {
        return block;
    }

    const startRecordingPhaseIndex = block.findIndex(
        (p) => p.type === ExamFlowPhases.START_RECORDING || p.type === ExamFlowPhases.STUDENT_START_RECORDING,
    );
    const encounterPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.ENCOUNTER);

    if (startRecordingPhaseIndex !== -1) {
        block.splice(startRecordingPhaseIndex, 0, buildEmrPhase());
    } else if (startRecordingPhaseIndex === -1 && encounterPhaseIndex !== -1) {
        block.splice(encounterPhaseIndex, 0, buildEmrPhase());
    } else {
        const doorNotesPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.DOOR_NOTES);
        const waitingRoomPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.WAITING_ROOM);

        let insertIndex = block.length;
        if (doorNotesPhaseIndex !== -1) {
            insertIndex = doorNotesPhaseIndex + 1;
        } else if (waitingRoomPhaseIndex !== -1) {
            insertIndex = waitingRoomPhaseIndex + 1;
        }

        block.splice(insertIndex, 0, buildEmrPhase());
    }

    const postEncounterPhase = block.find((p) => p.type === ExamFlowPhases.POST_ENCOUNTER);

    if (isNil(postEncounterPhase)) {
        block.push(buildPostEncounterPhase());
    }

    return block.map((phase) => {
        const propsToUpdate: Partial<FlowPlanPhase> = {};

        if (phase.type === ExamFlowPhases.ENCOUNTER) {
            propsToUpdate.views = phase.views;
            propsToUpdate.views.patient.showEmr = true;
        } else if (phase.type === ExamFlowPhases.POST_ENCOUNTER) {
            propsToUpdate.views = phase.views;
            propsToUpdate.views.learner.showEmr = true;
            propsToUpdate.inputs = phase.inputs;

            // if learner view was empty before, add an input for it
            if (isNil(propsToUpdate.views.learner.inputId)) {
                const learnerInputId = uuidv4();
                propsToUpdate.views.learner.inputId = learnerInputId;
                propsToUpdate.inputs[0].push(learnerInputId);
            }
        }
        return {
            ...phase,
            ...propsToUpdate,
        };
    });
};

export const switchToSystemAutomatedPostEncounterPhase = (block) => {
    const postEncounterPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.POST_ENCOUNTER);

    if (postEncounterPhaseIndex < 0) {
        return block;
    }

    const options = {
        hasLearnerEvaluation: getBlockHasLearnerEvaluation(block),
        hasPatientEvaluation: getBlockHasPatientEvaluation(block),
        hasEmr: getBlockHasEmr(block),
    };

    block.splice(postEncounterPhaseIndex, 1, buildSystemAutomatedPostEncounterPhase(options));

    return block;
};

export const switchToPostEncounterPhase = (block) => {
    const postEncounterPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.POST_ENCOUNTER);

    if (postEncounterPhaseIndex < 0) {
        return block;
    }

    const options = {
        hasLearnerEvaluation: getBlockHasLearnerEvaluation(block),
        hasPatientEvaluation: getBlockHasPatientEvaluation(block),
        hasEmr: getBlockHasEmr(block),
    };

    block.splice(postEncounterPhaseIndex, 1, buildPostEncounterPhase(options));

    return block;
};

export const enableSystemAutomatedEmr = (block) => {
    const emrPhase = block.find((p) => p.type === ExamFlowPhases.EMR);

    if (!isNil(emrPhase)) {
        return block;
    }

    const startRecordingPhaseIndex = block.findIndex(
        (p) => p.type === ExamFlowPhases.START_RECORDING || p.type === ExamFlowPhases.STUDENT_START_RECORDING,
    );
    const encounterPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.ENCOUNTER);

    let insertIndex: number;
    if (startRecordingPhaseIndex !== -1) {
        insertIndex = startRecordingPhaseIndex;
    } else if (encounterPhaseIndex !== -1) {
        insertIndex = encounterPhaseIndex;
    } else {
        // No encounter or start recording phase, determine position based on other phases
        const doorNotesPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.DOOR_NOTES);
        const waitingRoomPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.WAITING_ROOM);

        if (doorNotesPhaseIndex !== -1) {
            insertIndex = doorNotesPhaseIndex + 1;
        } else if (waitingRoomPhaseIndex !== -1) {
            insertIndex = waitingRoomPhaseIndex + 1;
        } else {
            insertIndex = 0;
        }
    }

    block.splice(insertIndex, 0, buildSystemAutomatedEmrPhase());

    const postEncounterPhase = block.find((p) => p.type === ExamFlowPhases.POST_ENCOUNTER);

    if (isNil(postEncounterPhase)) {
        block.push(buildSystemAutomatedPostEncounterPhase());
    }

    return block.map((phase) => {
        const propsToUpdate: Partial<FlowPlanPhase> = {};

        if (phase.type === ExamFlowPhases.ENCOUNTER) {
            propsToUpdate.views = phase.views;
            propsToUpdate.views.patient.showEmr = true;
        } else if (phase.type === ExamFlowPhases.POST_ENCOUNTER) {
            propsToUpdate.views = phase.views;
            propsToUpdate.views.learner.showEmr = true;
            propsToUpdate.inputs = phase.inputs;
        }
        return {
            ...phase,
            ...propsToUpdate,
        };
    });
};

/**
 * Idempotently makes a block learner driven.
 *
 * @param {Array} block
 * @param {Array}
 * @returns {Array}
 */
export const makeBlockLearnerDriven = (block) => {
    const learnerStartRecordingPhase = block.find((p) => p.type === ExamFlowPhases.STUDENT_START_RECORDING);

    if (!isNil(learnerStartRecordingPhase)) {
        return block;
    }

    const proctorStartRecordingPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.START_RECORDING);
    block.splice(proctorStartRecordingPhaseIndex, 1, buildLearnerStartRecordingPhase());

    return block;
};

/**
 * Idempotently makes a block proctor driven.
 *
 * @param {Array} block
 * @returns {Array}
 */
export const makeBlockProctorDriven = (block) => {
    const proctorStartRecordingPhase = block.find((p) => p.type === ExamFlowPhases.START_RECORDING);

    if (!isNil(proctorStartRecordingPhase)) {
        return block;
    }

    const learnerStartRecordingPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.STUDENT_START_RECORDING);
    block.splice(learnerStartRecordingPhaseIndex, 1, buildStartRecordingPhase());

    return block;
};

//#region create phases (copied from shared-exam-flow-templates)

type BuildPostEncounterPhaseOptions = {
    hasEmr?: boolean;
    hasLearnerEvaluation?: boolean;
    hasPatientEvaluation?: boolean;
    timers?: Array<FlowPlanTimer>;
};

/**
 * Builds an exam flow phase of the specified type.
 */
export const buildPhaseType = (
    type: ExamPhaseType,
    postEncounterOptions: BuildPostEncounterPhaseOptions = {},
): Partial<FlowPlanPhase> => {
    switch (type) {
        case ExamFlowPhases.WAITING_ROOM:
            return buildWaitingRoomPhase();
        case ExamFlowPhases.DOOR_NOTES:
            return buildDoorNotesPhase();
        case ExamFlowPhases.EMR:
            return buildEmrPhase();
        case ExamFlowPhases.START_RECORDING:
            return buildStartRecordingPhase();
        case ExamFlowPhases.STUDENT_START_RECORDING:
            return buildLearnerStartRecordingPhase();
        case ExamFlowPhases.ENCOUNTER:
            return buildEncounterPhase();
        case ExamFlowPhases.POST_ENCOUNTER:
            return buildPostEncounterPhase(postEncounterOptions);
        default:
            throw new Error('Unsupported phase type');
    }
};

/**
 * Builds a waiting room phase.
 */
export const buildWaitingRoomPhase = (): Partial<FlowPlanPhase> => {
    const proctorInputId = uuidv4();

    return {
        inputs: [[proctorInputId]],
        timers: [],
        type: ExamFlowPhases.WAITING_ROOM,
        views: {
            learner: {
                message: 'Wait for Announcement to Enter.',
            },
            patient: {
                message: 'Waiting for learner to enter.',
            },
            proctor: {
                inputId: proctorInputId,
            },
        },
    };
};

const buildSystemAutomatedDoorNotesPhase = (): Partial<FlowPlanPhase> => {
    const defaultDoorNotesTimer = {
        ...getDefaultDoorNotesTimer(),
        isHardTimer: true,
    };
    const proctorInputId = uuidv4();
    const timerInputId = defaultDoorNotesTimer.inputId;

    return {
        inputs: [[proctorInputId], [timerInputId]],
        timers: [defaultDoorNotesTimer],
        type: ExamFlowPhases.DOOR_NOTES,
        views: {
            learner: {},
            patient: {
                message: 'Waiting for learner to enter.',
            },
            proctor: {
                inputId: proctorInputId,
            },
        },
    };
};

/**
 * Builds a door notes phase.
 */
export const buildDoorNotesPhase = (): Partial<FlowPlanPhase> => {
    const learnerInputId = uuidv4();
    const proctorInputId = uuidv4();
    return {
        inputs: [[learnerInputId], [proctorInputId]],
        timers: [],
        type: ExamFlowPhases.DOOR_NOTES,
        views: {
            learner: {
                inputId: learnerInputId,
            },
            patient: {
                message: 'Waiting for learner to enter.',
            },
            proctor: {
                inputId: proctorInputId,
            },
        },
    };
};

/**
 * Builds an EMR phase.
 */
export const buildEmrPhase = (): Partial<FlowPlanPhase> => {
    const learnerInputId = uuidv4();
    const proctorInputId = uuidv4();
    return {
        inputs: [[proctorInputId], [learnerInputId]],
        timers: [],
        type: ExamFlowPhases.EMR,
        views: {
            learner: {
                showEmr: true,
                inputId: learnerInputId,
            },
            patient: {
                message: 'Waiting for learner to enter.',
            },
            proctor: {
                inputId: proctorInputId,
            },
        },
    };
};

const buildSystemAutomatedEmrPhase = (): Partial<FlowPlanPhase> => {
    const defaultEmrTimer = {
        ...getDefaultEmrTimer(),
        isHardTimer: true,
    };
    const proctorInputId = uuidv4();
    const timerInputId = defaultEmrTimer.inputId;

    return {
        inputs: [[proctorInputId], [timerInputId]],
        timers: [defaultEmrTimer],
        type: ExamFlowPhases.EMR,
        views: {
            learner: {
                showEmr: true,
            },
            patient: {
                message: 'Waiting for learner to enter.',
            },
            proctor: {
                inputId: proctorInputId,
            },
        },
    };
};

/**
 * Builds a start recording phase.
 *
 * @returns {object}
 */
export const buildStartRecordingPhase = (): Partial<FlowPlanPhase> => {
    const proctorInputId = uuidv4();

    return {
        inputs: [[proctorInputId]],
        isRecordingPhase: true,
        timers: [],
        type: ExamFlowPhases.START_RECORDING,
        views: {
            learner: {
                message: 'Wait for Announcement to Enter.',
            },
            patient: {
                message: 'Waiting for learner to enter.',
            },
            proctor: {
                inputId: proctorInputId,
            },
        },
    };
};

/**
 * Builds a learner start recording phase.
 *
 * @returns {object}
 */
export const buildLearnerStartRecordingPhase = (): Partial<FlowPlanPhase> => {
    const learnerInputId = uuidv4();
    const proctorInputId = uuidv4();

    return {
        inputs: [[learnerInputId], [proctorInputId]],
        isRecordingPhase: true,
        timers: [],
        type: ExamFlowPhases.STUDENT_START_RECORDING,
        views: {
            learner: {
                message: 'Click to Start Encounter.',
                inputId: learnerInputId,
            },
            patient: {
                message: 'Waiting for learner to enter.',
            },
            proctor: {
                inputId: proctorInputId,
            },
        },
    };
};

/**
 * Builds an encounter phase.
 */
export const buildEncounterPhase = (showEmr: boolean = false): Partial<FlowPlanPhase> => {
    const learnerInputId = uuidv4();
    const proctorInputId = uuidv4();
    const defaultEncounterTimer = getDefaultEncounterTimer();
    const timerInputId = defaultEncounterTimer.inputId;

    return {
        inputs: [[learnerInputId], [proctorInputId], [timerInputId]],
        isRecordingEnabled: true,
        isStopRecordingPhase: true,
        timers: [defaultEncounterTimer],
        type: 'encounter',
        views: {
            learner: {
                message: 'Click to finish Session.',
                inputId: learnerInputId,
            },
            patient: {
                showEmr,
            },
            proctor: {
                inputId: proctorInputId,
            },
        },
    };
};

/**
 * Builds a system automated encounter phase.
 */
export const buildSystemAutomatedEncounterPhase = (showEmr: boolean = false): Partial<FlowPlanPhase> => {
    const proctorInputId = uuidv4();
    const defaultEncounterTimer = {
        ...getDefaultEncounterTimer(),
        isHardTimer: true,
    };
    const timerInputId = defaultEncounterTimer.inputId;

    return {
        inputs: [[proctorInputId], [timerInputId]],
        isRecordingEnabled: true,
        isRecordingPhase: true,
        isStopRecordingPhase: true,
        timers: [defaultEncounterTimer],
        type: 'encounter',
        views: {
            learner: {},
            patient: {
                showEmr,
            },
            proctor: {
                inputId: proctorInputId,
            },
        },
    };
};

/**
 * Builds a post encounter phase.
 *
 * @param [options]
 */
export const buildPostEncounterPhase = (options: BuildPostEncounterPhaseOptions = {}): Partial<FlowPlanPhase> => {
    const learnerInputId = uuidv4();
    const patientInputId = uuidv4();
    const proctorInputId = uuidv4();

    const postEncounterPhase: Partial<FlowPlanPhase> = {
        inputs: [],
        timers: isNil(options.timers) ? [getDefaultPostEncounterTimer()] : options.timers,
        type: ExamFlowPhases.POST_ENCOUNTER,
        views: {
            learner: {
                message: 'Waiting for patient to complete evaluation.',
            },
            patient: {
                message: 'Waiting for learner to complete evaluation.',
            },
            proctor: {
                inputId: proctorInputId,
            },
        },
    };

    const learnerOrPatientInputIds = [];
    const proctorInputIds = [proctorInputId];
    const timerInputIds = [postEncounterPhase.timers[0].inputId]; // post encounters always have a timer

    if (options.hasLearnerEvaluation || options.hasEmr) {
        learnerOrPatientInputIds.push(learnerInputId);
        postEncounterPhase.views.learner.inputId = learnerInputId;

        if (options.hasLearnerEvaluation) {
            postEncounterPhase.views.learner.evaluationTemplateIds = [];
            postEncounterPhase.views.learner.hasEval = true;
            delete postEncounterPhase.views.learner.message;
        }
        if (options.hasEmr) {
            postEncounterPhase.views.learner.showEmr = true;
        }
    }
    if (options.hasPatientEvaluation) {
        learnerOrPatientInputIds.push(patientInputId);

        postEncounterPhase.views.patient = {
            evaluationTemplateIds: [],
            hasEval: true,
            inputId: patientInputId,
        };
    }

    // Populate the inputs array at the end.
    postEncounterPhase.inputs.push(learnerOrPatientInputIds, proctorInputIds, timerInputIds);

    return postEncounterPhase;
};

/**
 * Builds a system automated post encounter phase.
 *
 * @param [options]
 */
export const buildSystemAutomatedPostEncounterPhase = (
    options: BuildPostEncounterPhaseOptions = {},
): Partial<FlowPlanPhase> => {
    const defaultPostEncounterTimer = {
        ...getDefaultPostEncounterTimer(),
        isHardTimer: true,
    };
    const proctorInputId = uuidv4();
    const timers = isNil(options.timers) ? [defaultPostEncounterTimer] : options.timers;

    const postEncounterPhase: Partial<FlowPlanPhase> = {
        inputs: [],
        timers,
        type: ExamFlowPhases.POST_ENCOUNTER,
        views: {
            learner: {
                message: 'Waiting for patient to complete evaluation.',
            },
            patient: {
                message: 'Waiting for learner to complete evaluation.',
            },
            proctor: {
                inputId: proctorInputId,
            },
        },
    };

    const proctorInputIds = [proctorInputId];
    const timerInputIds = [postEncounterPhase.timers[0].inputId]; // post encounters always have a timer

    if (options.hasLearnerEvaluation || options.hasEmr) {
        if (options.hasLearnerEvaluation) {
            postEncounterPhase.views.learner.evaluationTemplateIds = [];
            postEncounterPhase.views.learner.hasEval = true;
            delete postEncounterPhase.views.learner.message;
        }
        if (options.hasEmr) {
            postEncounterPhase.views.learner.showEmr = true;
        }
    }
    if (options.hasPatientEvaluation) {
        postEncounterPhase.views.patient = {
            evaluationTemplateIds: [],
            hasEval: true,
        };
    }

    // Populate the inputs array at the end.
    postEncounterPhase.inputs.push(proctorInputIds, timerInputIds);

    return postEncounterPhase;
};

//#endregion create phases (copied from shared-exam-flow-templates)

/**
 * Creates a new timer with the default settings for the given phase type.
 *
 * @param phaseType
 */
export const getDefaultTimer = (phaseType: ExamPhaseType): FlowPlanTimer => {
    let timer;

    switch (phaseType) {
        case ExamFlowPhases.DOOR_NOTES:
            timer = getDefaultDoorNotesTimer();
            break;
        case ExamFlowPhases.EMR:
            timer = getDefaultEmrTimer();
            break;
        case ExamFlowPhases.ENCOUNTER:
            timer = getDefaultEncounterTimer();
            break;
        case ExamFlowPhases.POST_ENCOUNTER:
            timer = getDefaultPostEncounterTimer();
            break;
        default:
            throw new Error('Unsupported phase type');
    }

    return timer;
};

/**
 * Returns a new instance of a door notes timer with default settings.
 */
export const getDefaultDoorNotesTimer = (): FlowPlanTimer => {
    return {
        durationSeconds: 1 * 60, // 1 minute
        inputId: uuidv4(),
        isHardTimer: false,
        shouldHideTimer: {
            learner: false,
            sp: false,
        },
    };
};

/**
 * Returns a new instance of an EMR timer with default settings.
 */
export const getDefaultEmrTimer = (): FlowPlanTimer => {
    return {
        durationSeconds: 1 * 60, // 1 minute
        inputId: uuidv4(),
        isHardTimer: false,
        shouldHideTimer: {
            learner: false,
            sp: false,
        },
    };
};

/**
 * Returns a new instance of an encounter timer with default settings.
 */
export const getDefaultEncounterTimer = (): FlowPlanTimer => {
    return {
        durationSeconds: 20 * 60, // 20 minutes
        inputId: uuidv4(),
        isHardTimer: false,
        shouldHideTimer: {
            learner: false,
            sp: false,
        },
    };
};

/**
 * Returns a new instance of a post-encounter timer with default settings.
 */
export const getDefaultPostEncounterTimer = (): FlowPlanTimer => {
    return {
        durationSeconds: 10 * 60, // 10 minutes
        inputId: uuidv4(),
        isHardTimer: false,
        shouldHideTimer: {
            learner: false,
            sp: false,
        },
    };
};

/**
 * Idempotently ensures that a block's encounter, if any, is configured to be recorded.
 */
export const enableBlockEncounterRecording = (block: Array<FlowPlanPhase>): Array<FlowPlanPhase> => {
    const encounterPhase = block.find((p) => p.type === ExamFlowPhases.ENCOUNTER);

    if (!isNil(encounterPhase)) {
        encounterPhase.isRecordingEnabled = true;
    }

    return block;
};

/**
 * Idempotently ensures that a block's encounter, if any, is configured to not be recorded.
 */
export const disableBlockEncounterRecording = (block: Array<FlowPlanPhase>): Array<FlowPlanPhase> => {
    const encounterPhase = block.find((p) => p.type === ExamFlowPhases.ENCOUNTER);

    if (!isNil(encounterPhase)) {
        encounterPhase.isRecordingEnabled = false;
    }

    return block;
};

export const getShouldHideTimerLearner = (timer: FlowPlanTimer): boolean => {
    if (isNil(timer?.shouldHideTimer)) {
        return false;
    }

    if (typeof timer.shouldHideTimer === 'boolean') {
        return timer.shouldHideTimer;
    } else {
        return timer.shouldHideTimer.learner;
    }
};

export const getShouldHideTimerPatient = (timer: FlowPlanTimer): boolean => {
    if (isNil(timer?.shouldHideTimer)) {
        return false;
    }

    if (typeof timer.shouldHideTimer === 'boolean') {
        return timer.shouldHideTimer;
    } else {
        return timer.shouldHideTimer.sp;
    }
};

export const enableInRoomEvaluation = (
    block: Array<Partial<FlowPlanPhase>>,
    isSystemAutomated: boolean,
): Array<Partial<FlowPlanPhase>> => {
    const encounterPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.ENCOUNTER);

    if (encounterPhaseIndex < 0) {
        if (isSystemAutomated) {
            block.splice(encounterPhaseIndex, 1, buildSystemAutomatedEncounterPhase() as FlowPlanPhase);
        } else {
            block.splice(encounterPhaseIndex, 1, buildEncounterPhase() as FlowPlanPhase);
        }
    }

    const encounterPhase = block[encounterPhaseIndex];

    encounterPhase.views.patient.hasEval = true;

    return block;
};

export const disableInRoomEvaluation = (block: Array<FlowPlanPhase>): Array<FlowPlanPhase> => {
    const encounterPhaseIndex = block.findIndex((p) => p.type === ExamFlowPhases.ENCOUNTER);

    if (encounterPhaseIndex < 0) {
        return block;
    }

    const encounterPhase = block[encounterPhaseIndex];

    if (encounterPhase.views.patient.hasEval === true) {
        delete encounterPhase.views.patient.hasEval;
        delete encounterPhase.views.patient.evaluationTemplateIds;
    }

    return block;
};

export const flowPlanHasInRoomEvaluation = (flowPlanBlocks: Array<Array<FlowPlanPhase>>): boolean => {
    return flowPlanBlocks.some((block) => {
        const encounterPhase = block.find((p) => p.type === ExamFlowPhases.ENCOUNTER);
        return encounterPhase?.views.patient.hasEval === true;
    });
};

export const createNormalizedFlowPlan = (flowPlan: Array<FlowPlanPhase>) => {
    const recordingPhases: ExamFlowPhase[] = [ExamFlowPhases.START_RECORDING, ExamFlowPhases.STUDENT_START_RECORDING];
    const flowPlanNormalized = structuredClone(flowPlan);

    for (let index = 0; index < flowPlanNormalized.length; index++) {
        const phase = flowPlanNormalized[index];
        const nextPhase = flowPlanNormalized[index + 1];

        if (
            recordingPhases.includes(phase.type) &&
            nextPhase?.type === ExamFlowPhases.ENCOUNTER &&
            !nextPhase.isRecordingPhase
        ) {
            delete phase.isRecordingPhase;
            nextPhase.isRecordingPhase = true;
        }
    }

    return flowPlanNormalized;
};
